import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { api, apiError, currencyFmt } from "@/lib/api";
import { fmtDateOnly } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DateField } from "@/components/DateField";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ReceiptText, Download, X, Search } from "lucide-react";
import { toast } from "sonner";

const toKey = (d) => (d ? format(d, "yyyy-MM-dd") : "");

export default function Claims() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState(undefined);
  const [to, setTo] = useState(undefined);
  const [search, setSearch] = useState("");
  const [branch, setBranch] = useState("all");
  const [branches, setBranches] = useState([]);
  const [expenseType, setExpenseType] = useState("all");

  const buildParams = () => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (from) params.date_from = toKey(from);
    if (to) params.date_to = toKey(to);
    if (branch !== "all") params.branch = branch;
    if (expenseType !== "all") params.expense_type = expenseType;
    return params;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/claims", { params: buildParams() });
        setClaims(data);
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, from, to, branch, expenseType]);

  const isEmployee = user.role === "employee";
  const canReview = ["manager", "admin", "branch_manager"].includes(user.role);
  const hasDates = from || to;

  useEffect(() => {
    if (canReview) api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, [canReview]);

  const sq = search.trim().toLowerCase();
  const visibleClaims = sq
    ? claims.filter((c) =>
        (c.title || "").toLowerCase().includes(sq) ||
        (c.employee_name || "").toLowerCase().includes(sq) ||
        (c.claim_number || "").toLowerCase().includes(sq))
    : claims;

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/claims/export", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "claims_export.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="claims-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
            {isEmployee ? "My Claims" : canReview ? "Claims & Approvals" : "All Claims"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {canReview ? "Review and act on reimbursement requests." : "Track your submitted reimbursement claims."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
          {(isEmployee || ["manager", "branch_manager"].includes(user.role)) && (
            <Button className="rounded-sm" onClick={() => navigate("/claims/new")} data-testid="new-claim-btn">
              <Plus className="h-4 w-4 mr-2" />New Claim
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="all" data-testid="filter-all" className="rounded-sm">All</TabsTrigger>
            <TabsTrigger value="pending" data-testid="filter-pending" className="rounded-sm">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="filter-approved" className="rounded-sm">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="filter-rejected" className="rounded-sm">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          {canReview && (
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="claims-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                <SelectItem value="No branch">No branch</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={expenseType} onValueChange={setExpenseType}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="claims-expense-type-filter"><SelectValue placeholder="Expense type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All expense types</SelectItem>
              <SelectItem value="personal">Personal Expense Claim</SelectItem>
              <SelectItem value="company_credit_card">Company Credit Card</SelectItem>
              <SelectItem value="company_debit_card">Company Debit Card</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">Expense date</span>
          <DateField value={from} onChange={setFrom} placeholder="From" testId="claims-date-from" className="w-40" />
          <DateField value={to} onChange={setTo} placeholder="To" testId="claims-date-to" className="w-40" />
          {hasDates && (
            <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => { setFrom(undefined); setTo(undefined); }} data-testid="claims-date-clear">
              <X className="h-4 w-4 mr-1.5" />Clear
            </Button>
          )}
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title, employee or claim number…" className="pl-9 rounded-sm" data-testid="claim-search" />
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : visibleClaims.length === 0 ? (
          <div className="p-12 text-center">
            <ReceiptText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">{claims.length === 0 ? "No claims found." : "No claims match your search."}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Claim</TableHead>
                <TableHead>Title</TableHead>
                {!isEmployee && <TableHead>Employee</TableHead>}
                <TableHead>Expense Type</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleClaims.map((c) => (
                <TableRow key={c.claim_id} className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/claims/${c.claim_id}`)} data-testid={`claim-row-${c.claim_id}`}>
                  <TableCell className="font-mono text-xs">{c.claim_number}</TableCell>
                  <TableCell className="font-medium max-w-[220px] truncate">{c.title}</TableCell>
                  {!isEmployee && <TableCell className="text-sm">{c.employee_name}</TableCell>}
                  <TableCell className="text-sm text-muted-foreground">{c.expense_type_label || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.branch_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDateOnly(c.expense_date)}</TableCell>
                  <TableCell className="text-right font-mono font-medium">{currencyFmt(c.total_amount, c.currency)}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
